import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock,
  MapPin,
  KeyRound,
  Wifi,
  Clock,
  Car,
  MessageCircle,
  Copy,
  Check,
  ExternalLink,
  Tv,
  Route,
  Download,
  Navigation2,
  Play,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { checkinLookup } from "../lib/api";
import { PROPERTIES } from "../lib/properties";

function CopyBtn({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-[#111] hover:bg-[#f2eeea]"
      aria-label={`Copy ${label}`}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

function DetailRow({ icon: Icon, label, value, copy, extra }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#f0f0f0] last:border-0">
      <span className="w-9 h-9 shrink-0 rounded-xl bg-[#F4F4F4] flex items-center justify-center text-[#111]">
        <Icon size={16} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8a8a8a] font-semibold">
          {label}
        </p>
        <p className="mt-0.5 text-[15px] text-[#111] break-words leading-snug">
          {value}
        </p>
        {extra}
      </div>
      {copy && <CopyBtn value={value} label={label} />}
    </div>
  );
}

export default function CheckIn() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [nestId, setNestId] = useState(null);
  const [details, setDetails] = useState(null);
  const [guestName, setGuestName] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    checkinLookup(slug)
      .then((r) => {
        setNestId(r.nest_id);
        setDetails(r.details);
        setGuestName((r.guest_label || "").split(/[·|,]/)[0].trim() || null);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const nest = PROPERTIES.find((p) => p.id === nestId);

  const handleDownloadPdf = () => {
    if (!details) return;
    setDownloading(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const marginX = 48;
      const contentW = pageW - marginX * 2;
      let y = 56;

      const line = (text, opts = {}) => {
        const { size = 11, bold = false, color = "#111111", gap = 6 } = opts;
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.setTextColor(color);
        const chunks = doc.splitTextToSize(text || "", contentW);
        chunks.forEach((chunk) => {
          if (y > 780) {
            doc.addPage();
            y = 56;
          }
          doc.text(chunk, marginX, y);
          y += size * 1.25;
        });
        y += gap;
      };

      // Header
      line("SELF CHECK-IN", { size: 9, color: "#888888", gap: 2 });
      if (guestName) line(`Hi! ${guestName}`, { size: 14, bold: true, gap: 2 });
      line(nest?.fullName || nest?.name || "Your nest", { size: 22, bold: true, gap: 2 });
      if (nest?.location) line(nest.location, { size: 11, color: "#666", gap: 14 });

      // Details block
      const rows = [
        ["Address", details.address],
        ["Access code", details.door_code],
        ["Wi-Fi name", details.wifi_name],
        ["Wi-Fi password", details.wifi_password],
        ["Netflix profile", details.netflix_profile],
        ["Netflix PIN", details.netflix_pin],
        [
          "Check-in / Check-out",
          details.checkin_time || details.checkout_time
            ? `${details.checkin_time || "—"} in · ${details.checkout_time || "—"} out`
            : "",
        ],
        ["Parking", details.parking],
        ["Host contact", details.host_contact],
      ];
      rows.forEach(([label, value]) => {
        if (!value) return;
        line(label.toUpperCase(), { size: 8, color: "#999999", gap: 2 });
        line(String(value), { size: 12, gap: 10 });
      });

      // Arrival instructions
      if (details.arrival_instructions) {
        y += 6;
        line("GETTING IN — STEP BY STEP", {
          size: 9,
          bold: true,
          color: "#111",
          gap: 6,
        });
        details.arrival_instructions.split("\n").forEach((ln) => {
          if (ln.trim() === "") {
            y += 4;
          } else {
            line(ln, { size: 11, gap: 4 });
          }
        });
        y += 8;
      }

      // House rules
      const rules = Array.isArray(details.house_rules) ? details.house_rules : [];
      if (rules.length) {
        line("HOUSE RULES", { size: 9, bold: true, color: "#111", gap: 6 });
        rules.forEach((r) => line(`• ${r}`, { size: 11, gap: 3 }));
      }

      // Footer
      y = Math.max(y, 800);
      doc.setFontSize(8);
      doc.setTextColor("#a1a1a1");
      doc.text("hostbydims · self check-in", marginX, 820);

      const filename = `hostbydims-${(nest?.fullName || nest?.name || "checkin").replace(/[\s/]+/g, "_")}.pdf`;
      doc.save(filename);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <section className="px-5 pt-20 text-center text-[#8a8a8a]">
        Loading…
      </section>
    );
  }

  if (notFound || !details) {
    return (
      <section className="px-5 pt-16 pb-24 text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F4F4F4] flex items-center justify-center text-[#111]">
          <Lock size={20} />
        </div>
        <h1 className="font-heading text-2xl font-extrabold text-[#111] tracking-tighter mt-5">
          Link not found
        </h1>
        <p className="text-sm text-[#5b5b5b] mt-2">
          This check-in link doesn&rsquo;t exist or has been revoked. Please
          message your host for a new one.
        </p>
      </section>
    );
  }

  const rules = Array.isArray(details.house_rules) ? details.house_rules : [];
  const contact = details.host_contact || "";
  let contactHref = "";
  if (contact) {
    const digits = contact.replace(/\D/g, "");
    if (contact.startsWith("http")) contactHref = contact;
    else if (digits.length >= 8) contactHref = `https://wa.me/${digits}`;
    else contactHref = `tel:${contact}`;
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-5 pt-8 pb-24"
      data-testid="checkin-details"
    >
      <div className="max-w-md mx-auto">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#111] font-semibold">
          Self check-in
        </p>
        {guestName && (
          <p
            data-testid="checkin-greeting"
            className="font-heading text-lg font-bold text-[#111] tracking-tight mt-3"
          >
            Hi! {guestName}
          </p>
        )}
        <h1 className="font-heading text-3xl font-extrabold text-[#111] tracking-tighter mt-2">
          {nest?.fullName || nest?.name || "Your nest"}
        </h1>
        {nest?.location && (
          <p className="text-sm text-[#5b5b5b] mt-1">{nest.location}</p>
        )}

        {/* Big prominent "Get Directions" button — opens Google Maps in driving mode with live traffic */}
        {details.address && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(details.address)}&travelmode=driving`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="checkin-directions-btn"
            className="relative mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#111111] text-white py-3 px-5 text-sm font-semibold overflow-hidden group"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
            </span>
            <Navigation2 size={15} className="animate-pulse" />
            Get Directions · Live traffic
          </a>
        )}

        <div className="gb-card p-5 sm:p-6 mt-4">
          <DetailRow
            icon={MapPin}
            label="Address"
            value={details.address}
            copy
          />
          <DetailRow
            icon={KeyRound}
            label="Access code"
            value={details.door_code}
            copy
          />
          <DetailRow
            icon={Wifi}
            label="Wi-Fi name"
            value={details.wifi_name}
            copy
          />
          <DetailRow
            icon={Wifi}
            label="Wi-Fi password"
            value={details.wifi_password}
            copy
          />
          <DetailRow
            icon={Tv}
            label="Netflix profile"
            value={details.netflix_profile}
          />
          <DetailRow
            icon={Tv}
            label="Netflix PIN"
            value={details.netflix_pin}
            copy
          />
          <DetailRow
            icon={Clock}
            label="Check-in / Check-out"
            value={
              details.checkin_time || details.checkout_time
                ? `${details.checkin_time || "—"} in · ${details.checkout_time || "—"} out`
                : ""
            }
          />
          <DetailRow icon={Car} label="Parking" value={details.parking} />
        </div>

        {details.arrival_instructions && (
          <div className="gb-card p-5 sm:p-6 mt-4">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-[#F4F4F4] flex items-center justify-center text-[#111]">
                <Route size={16} />
              </span>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#111] font-semibold">
                Getting in — step by step
              </p>
            </div>
            <div className="mt-3 text-[14px] text-[#222] leading-relaxed">
              {(() => {
                const paragraphs = details.arrival_instructions.split(/\n\s*\n/);
                const imagesByStep = {};
                (details.arrival_images || []).forEach((im) => {
                  if (!imagesByStep[im.step]) imagesByStep[im.step] = [];
                  imagesByStep[im.step].push(im);
                });
                const videosByStep = {};
                (details.videos || []).forEach((v) => {
                  if (v.step) {
                    if (!videosByStep[v.step]) videosByStep[v.step] = [];
                    videosByStep[v.step].push(v);
                  }
                });
                return paragraphs.map((p, idx) => {
                  const match = p.match(/^\s*(\d+)\./);
                  const stepNum = match ? parseInt(match[1], 10) : null;
                  const imgs = stepNum ? imagesByStep[stepNum] || [] : [];
                  const vids = stepNum ? videosByStep[stepNum] || [] : [];
                  return (
                    <div key={idx} className={idx > 0 ? "mt-4" : ""}>
                      <p className="whitespace-pre-line">{p}</p>
                      {imgs.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {imgs.map((im, i) => (
                            <figure
                              key={i}
                              data-testid={`arrival-img-step-${stepNum}-${i}`}
                            >
                              <img
                                src={im.url}
                                alt={im.caption || `Step ${stepNum}`}
                                loading="lazy"
                                className="w-full rounded-2xl border border-[#EBEBEB]"
                              />
                              {im.caption && (
                                <figcaption className="text-[11px] text-[#8a8a8a] mt-1.5 text-center">
                                  {im.caption}
                                </figcaption>
                              )}
                            </figure>
                          ))}
                        </div>
                      )}
                      {vids.length > 0 && (
                        <div className="mt-3 space-y-3">
                          {vids.map((v, i) => (
                            <div
                              key={i}
                              data-testid={`arrival-video-step-${stepNum}-${i}`}
                            >
                              <video
                                src={v.url}
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full rounded-2xl bg-black"
                              />
                              {v.title && (
                                <p className="text-[11px] text-[#8a8a8a] mt-1.5 text-center">
                                  {v.title}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {(() => {
          const unassigned = (details.videos || []).filter((v) => !v.step);
          if (unassigned.length === 0) return null;
          return (
            <div className="gb-card p-5 sm:p-6 mt-4">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-[#F4F4F4] flex items-center justify-center text-[#111]">
                  <Play size={16} />
                </span>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#111] font-semibold">
                  Walkthrough videos
                </p>
              </div>
              <div className="mt-4 space-y-4">
                {unassigned.map((v, i) => (
                  <div key={i} data-testid={`checkin-video-${i}`}>
                    {v.title && (
                      <p className="text-[13px] font-medium text-[#111] mb-2">
                        {v.title}
                      </p>
                    )}
                    <video
                      src={v.url}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded-2xl bg-black"
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {rules.length > 0 && (
          <div className="gb-card p-5 sm:p-6 mt-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#111] font-semibold">
              House rules
            </p>
            <ul className="mt-3 space-y-2">
              {rules.map((r, i) => (
                <li
                  key={i}
                  className="flex gap-2 text-[14px] text-[#222] leading-snug"
                >
                  <span className="text-[#8a8a8a]">·</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {contact && (
          <a
            href={contactHref}
            data-testid="checkin-contact-btn"
            className="gb-btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
            target={contactHref.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
          >
            <MessageCircle size={16} /> Message host
          </a>
        )}
        <button
          type="button"
          data-testid="checkin-download-pdf"
          onClick={handleDownloadPdf}
          disabled={downloading}
          className="gb-btn-ghost w-full mt-3 inline-flex items-center justify-center gap-2 text-sm"
        >
          <Download size={14} />
          {downloading ? "Preparing PDF…" : "Download as PDF"}
        </button>
      </div>
    </motion.section>
  );
}
